import { useEffect, useState } from 'react';
import './css/Entidade.css';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import DemandaModal from './components/modals/DemandaModal';
import DemandaItemModal from './components/modals/DemandaItemModal';
import usePagination10 from './components/hooks/usePagination10';

function Demandas() {
  const [demandas, setDemandas] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroTipoServico, setFiltroTipoServico] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'dataCriacao', direction: 'desc' });
  const [atualizar, setAtualizar] = useState(false);
  const [demandaSelecionada, setDemandaSelecionada] = useState(null);
  const [showDemandaModal, setShowDemandaModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingDemanda, setEditingDemanda] = useState(null);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [entidades, setEntidades] = useState([]);
  const token = localStorage.getItem('token');
  const [isLoading, setLoading] = useState(true);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const navigate = useNavigate();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  const usuario = JSON.parse(localStorage.getItem('user') || '{}');
  const vinculo = JSON.parse(localStorage.getItem('vinculo') || '{}');
  const orgaoInfo = JSON.parse(localStorage.getItem('orgaoInfo') || '{}');
  const isOrgaoUser = usuario?.tipoUsuario === 'ORGAO';
  const anoBase = Number(localStorage.getItem('anoBase')) || new Date().getFullYear();

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    setLoading(true);

    const promises = [
      axios.get(`${API_BASE_URL.DEMANDA}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${API_BASE_URL.USUARIO}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${API_BASE_URL.ENTIDADE}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ];

    Promise.all(promises)
      .then(([demandasRes, usuariosRes, entidadesRes]) => {
        let dados = demandasRes.data;
        dados = dados.filter(d => d.anoBase === anoBase);

        if (isOrgaoUser && usuario?.cpf) {
          dados = dados.filter(d => d.responsavelCpf === usuario.cpf);
        } else if (vinculo?.tipo !== 'ADMIN' && usuario?.cpf) {
          dados = dados.filter(
            d =>
              d.responsavelCpf === usuario.cpf ||
              d.criadoPorCpf === usuario.cpf
          );
        }

        setDemandas(dados);
        setUsuarios(usuariosRes.data);
        setEntidades(entidadesRes.data);
      })
      .catch(error => {
        console.error('Erro ao obter demandas:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar]);

  function handleDelete(d) {
    showConfirmModal('delete', () => handleConfirmDelete(d));
  }

  function podeExcluir(d) {
    if (!d) return false;
    if (vinculo?.tipo === 'ADMIN') return true;
    if (d.criadoPorCpf === usuario.cpf) {
      const criacao = new Date(d.dataCriacao);
      const agora = new Date();
      const minutos = (agora - criacao) / (1000 * 60);
      return minutos <= 10;
    }
    return false;
  }

  function handleConfirmDelete(d) {
    axios
      .delete(`${API_BASE_URL.DEMANDA}/delete/${d.id}?cpf=${usuario.cpf}&role=${vinculo.tipo || 'USER'}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        setAtualizar(prev => !prev);
        setDemandaSelecionada(null);
      })
      .catch(error => {
        const msg = error.response?.data || 'Erro ao excluir o registro!';
        showDefaultModal('error', typeof msg === 'string' ? msg : 'Erro ao excluir o registro!');
      });
  }

  const normalizeString = str => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  const getNomeEntidade = cnpj => {
    const e = entidades.find(ent => ent.cnpj === cnpj);
    return e ? e.nome : cnpj;
  };

  const getNomeUsuario = cpf => {
    const u = usuarios.find(usr => usr.cpf === cpf);
    return u ? u.nome : cpf || '-';
  };

  const handleSort = key => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = key => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up ms-1" style={{ fontSize: '0.65rem', opacity: 0.4 }}></i>;
    return sortConfig.direction === 'asc'
      ? <i className="bi bi-arrow-up ms-1" style={{ fontSize: '0.65rem' }}></i>
      : <i className="bi bi-arrow-down ms-1" style={{ fontSize: '0.65rem' }}></i>;
  };

  const demandasFiltradas = Array.isArray(demandas)
    ? demandas.filter(d => {
        const busca = normalizeString(filtro);
        const matchBusca =
          !busca ||
          normalizeString(d.titulo || '').includes(busca) ||
          normalizeString(getNomeEntidade(d.entidadeCnpj)).includes(busca) ||
          normalizeString(d.descricao || '').includes(busca);

        const matchStatus = !filtroStatus || d.status === filtroStatus;
        const matchTipoServico =
          !filtroTipoServico || d.tipoServico === filtroTipoServico;

        return matchBusca && matchStatus && matchTipoServico;
      })
    : [];

  const demandasOrdenadas = [...demandasFiltradas].sort((a, b) => {
    let valA = a[sortConfig.key];
    let valB = b[sortConfig.key];
    if (valA == null) valA = '';
    if (valB == null) valB = '';
    if (typeof valA === 'string') valA = normalizeString(valA);
    if (typeof valB === 'string') valB = normalizeString(valB);
    if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
    if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const totalItems = demandasOrdenadas.length;
  const { setPagination } = usePagination10(totalItems);
  const selectedDemandas = demandasOrdenadas.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const getStatusBadge = status => {
    const badges = {
      ABERTA: 'bg-primary',
      EM_ANDAMENTO: 'bg-warning text-dark',
      CONCLUIDA: 'bg-success',
      CANCELADA: 'bg-secondary',
    };
    return badges[status] || 'bg-secondary';
  };

  const getPrioridadeBadge = prioridade => {
    const badges = {
      URGENTE: 'bg-danger',
      ALTA: 'bg-warning text-dark',
      MEDIA: 'bg-info',
      BAIXA: 'bg-secondary',
    };
    return badges[prioridade] || 'bg-secondary';
  };

  const formatarData = data => {
    if (!data) return '-';
    const d = new Date(String(data).includes('T') ? data : data + 'T12:00:00');
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const demandaConcluida = demandaSelecionada?.status === 'CONCLUIDA';

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Demandas / Tarefas" icon="bi-clipboard-check" />
        <PageContent>
          <div className="d-flex align-items-center mb-2 flex-wrap gap-2" style={{ fontSize: '0.8rem' }}>
            {!isOrgaoUser && (
              <>
                <button
                  className="btn btn-success btn-sm"
                  title="Nova demanda"
                  onClick={() => {
                    setEditingDemanda(null);
                    setShowDemandaModal(true);
                  }}
                >
                  <i className="bi bi-plus-circle me-1"></i>Nova
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={!demandaSelecionada || vinculo?.tipo !== 'ADMIN'}
                  onClick={() => {
                    if (demandaSelecionada) {
                      setEditingDemanda(demandaSelecionada);
                      setReadOnlyMode(false);
                      setShowDemandaModal(true);
                    }
                  }}
                  title="Editar demanda selecionada"
                >
                  <i className="bi bi-pencil me-1"></i>Editar
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  disabled={!demandaSelecionada || !podeExcluir(demandaSelecionada) || demandaConcluida || vinculo?.tipo !== 'ADMIN'}
                  onClick={() => handleDelete(demandaSelecionada)}
                  title="Excluir demanda selecionada"
                >
                  <i className="bi bi-trash me-1"></i>Excluir
                </button>
              </>
            )}
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={!demandaSelecionada}
              onClick={() => {
                if (demandaSelecionada) {
                  setEditingDemanda(demandaSelecionada);
                  setReadOnlyMode(true);
                  setShowDemandaModal(true);
                }
              }}
              title="Visualizar demanda"
            >
              <i className="bi bi-eye me-1"></i>Visualizar
            </button>
            <button
              className="btn btn-info btn-sm text-white"
              disabled={!demandaSelecionada}
              onClick={() => {
                if (demandaSelecionada) {
                  setShowItemModal(true);
                }
              }}
              title="Gerenciar tópicos da demanda"
            >
              <i className="bi bi-list-check me-1"></i>Tópicos
            </button>
            {!isOrgaoUser && vinculo?.tipo === 'ADMIN' && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  const idEntidade = vinculo?.entidade?.idEntidade || vinculo?.idEntidade;
                  if (idEntidade) {
                    setIsGeneratingReport(true);
                    const url = `${API_BASE_URL.DEMANDA}/relatorio/${idEntidade}?anoBase=${anoBase}`;
                    const pdfId = `relatorio_${Date.now()}`;
                    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                      .then(res => res.blob())
                      .then(blob => {
                        if (!window.pdfStorage) window.pdfStorage = {};
                        window.pdfStorage[pdfId] = blob;
                        const blobUrl = URL.createObjectURL(blob);
                        sessionStorage.setItem('pdfViewerData', JSON.stringify({
                          url: blobUrl,
                          title: `Relatório de Atendimentos - ${anoBase}`,
                          pdfId,
                        }));
                        window.dispatchEvent(new Event('pdfViewerUpdate'));
                      })
                      .catch(() => showDefaultModal('error', 'Erro ao gerar relatório!'))
                      .finally(() => setIsGeneratingReport(false));
                  }
                }}
                title="Gerar relatório PDF de atendimentos"
              >
                <i className="bi bi-file-earmark-pdf me-1"></i>Relatório
              </button>
            )}
            <span className="text-muted">|</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', fontSize: '0.8rem', height: '31px' }}
              value={filtroTipoServico}
              onChange={e => setFiltroTipoServico(e.target.value)}
            >
              <option value="">Todos os Tipos</option>
              <option value="INTERNO">Interno</option>
              <option value="EXTERNO">Externo</option>
            </select>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', fontSize: '0.8rem', height: '31px' }}
              value={filtroStatus}
              onChange={e => setFiltroStatus(e.target.value)}
            >
              <option value="">Todos os Status</option>
              <option value="ABERTA">Aberta</option>
              <option value="EM_ANDAMENTO">Em Andamento</option>
              <option value="CONCLUIDA">Concluída</option>
              <option value="CANCELADA">Cancelada</option>
            </select>
            <div className="d-flex align-items-stretch" style={{ height: '31px' }}>
              <span className="d-flex align-items-center px-2 border border-end-0 rounded-start" style={{ background: '#e9ecef', fontSize: '0.75rem' }}>
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control form-control-sm border rounded-end"
                style={{ width: '180px', fontSize: '0.8rem' }}
                placeholder="Buscar..."
                value={filtro}
                onChange={e => setFiltro(e.target.value.toUpperCase())}
              />
            </div>
          </div>

          <TableContainer isLoading={isLoading}>
            <DataTable
              style={{ fontSize: '0.8rem' }}
              head={
                <tr>
                  <th scope="col" className="px-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('titulo')}>
                    Título{getSortIcon('titulo')}
                  </th>
                  <th scope="col" className="px-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('tipoAtendimento')}>
                    Atendimento{getSortIcon('tipoAtendimento')}
                  </th>
                  <th scope="col" className="px-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>
                    Status{getSortIcon('status')}
                  </th>
                  <th scope="col" className="px-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('prioridade')}>
                    Prioridade{getSortIcon('prioridade')}
                  </th>
                  <th scope="col" className="px-2" style={{ cursor: 'pointer' }} onClick={() => handleSort(isOrgaoUser ? 'entidadeCnpj' : null)}>
                    {isOrgaoUser ? 'Demandante' : 'Demandado'}{isOrgaoUser && getSortIcon('entidadeCnpj')}
                  </th>
                  <th scope="col" className="px-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('dataPrazo')}>
                    Prazo{getSortIcon('dataPrazo')}
                  </th>
                  <th scope="col" className="px-2">
                    Progresso
                  </th>
                </tr>
              }
            >
              <tbody>
                {!isLoading &&
                  selectedDemandas.map(d => (
                    <tr
                      key={d.id}
                      className={
                        demandaSelecionada && demandaSelecionada.id === d.id
                          ? 'table-primary'
                          : ''
                      }
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (
                          demandaSelecionada &&
                          demandaSelecionada.id === d.id
                        ) {
                          setDemandaSelecionada(null);
                        } else {
                          setDemandaSelecionada(d);
                        }
                      }}
                    >
                      <td className="px-2 fw-semibold">{d.titulo}</td>
                      <td className="px-2">
                        <span className={`badge ${d.tipoAtendimento === 'VIRTUAL' ? 'bg-info' : 'bg-secondary'}`}>
                          {d.tipoAtendimento === 'VIRTUAL' ? 'Virtual' : 'Presencial'}
                        </span>
                      </td>
                      <td className="px-2">
                        <span className={`badge ${getStatusBadge(d.status)}`}>
                          {d.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-2">
                        <span className={`badge ${getPrioridadeBadge(d.prioridade)}`}>
                          {d.prioridade}
                        </span>
                      </td>
                      <td className="px-2" style={isOrgaoUser ? {} : { lineHeight: '1.3' }}>
                        {!isOrgaoUser && vinculo?.tipo === 'ADMIN' && d.criadoPorCpf !== usuario.cpf && (
                          <div style={{ fontStyle: 'italic' }}>{getNomeUsuario(d.criadoPorCpf)}</div>
                        )}
                        {!isOrgaoUser && (
                          <>
                            <div>{d.orgaoNome || 'DEMANDA INTERNA'}</div>
                            <div style={{ fontSize: '0.7rem' }}><i className="bi bi-person me-1"></i>{getNomeUsuario(d.responsavelCpf)}</div>
                          </>
                        )}
                        {isOrgaoUser && (
                          <div>{getNomeEntidade(d.entidadeCnpj)}</div>
                        )}
                      </td>
                      <td className="px-2">
                        {formatarData(d.dataPrazo)}
                      </td>
                      <td className="px-2">
                        {d.totalItens > 0 ? (
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="progress flex-grow-1"
                              style={{ height: '6px', minWidth: '60px' }}
                            >
                              <div
                                className="progress-bar bg-success"
                                style={{
                                  width: `${(d.itensConcluidos / d.totalItens) * 100}%`,
                                }}
                              ></div>
                            </div>
                            <small className="text-muted">
                              {d.itensConcluidos}/{d.totalItens}
                            </small>
                          </div>
                        ) : (
                          <small className="text-muted">Sem tópicos</small>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </DataTable>
          </TableContainer>
          <ConfirmModal {...setConfirmModal} />
          <DefaultModal {...setDefaultModal} />
          <LoadingModal loading={isGeneratingReport} message="Gerando relatório, por favor aguarde..." />
          <DemandaModal
            show={showDemandaModal}
            onClose={() => {
              setShowDemandaModal(false);
              setEditingDemanda(null);
              setReadOnlyMode(false);
            }}
            onSuccess={() => {
              setShowDemandaModal(false);
              setEditingDemanda(null);
              setAtualizar(prev => !prev);
            }}
            demanda={editingDemanda}
            entidades={entidades}
            usuarios={usuarios}
            readOnly={readOnlyMode || editingDemanda?.status === 'CONCLUIDA'}
          />
          {demandaSelecionada && (
            <DemandaItemModal
              show={showItemModal}
              onClose={() => setShowItemModal(false)}
              onSuccess={() => {
                setShowItemModal(false);
                setAtualizar(prev => !prev);
              }}
              demanda={demandaSelecionada}
              usuarios={usuarios}
            />
          )}
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
    </Layout>
  );
}

export default Demandas;
